import React, { useRef, useState, useCallback } from "react";
import {
    intl,
    DmsTable,
    DmsButton,
    DmsPermissions,
    DmsFlexContainer,
    DmsButtonGroup,
    fetchData,
} from "dmsbasereact";
import Swal from "sweetalert2";
import { Typography, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Permissions } from "../../constants/Permissions";
import { PatientStatusReason } from "../../constants/PatientStatusReason";
import { PatientStatusReasonFormModal } from "../PatientStatusReasonFormModal";

export const PatientStatusReasonTable = ({ onReasonsChange }) => {
    const table = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedReason, setSelectedReason] = useState(null);

    const handleOpenCreate = useCallback(() => {
        setSelectedReason(null);
        setModalOpen(true);
    }, []);

    const handleOpenEdit = useCallback((reason) => {
        setSelectedReason(reason);
        setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        setSelectedReason(null);
    }, []);

    const handleSave = useCallback(() => {
        if (table.current) {
            table.current.updateTable();
        }
        if (onReasonsChange) {
            onReasonsChange();
        }
    }, [onReasonsChange]);

    const handleDelete = useCallback(async (reason) => {
        const result = await Swal.fire({
            icon: "warning",
            title: intl.get("ConfirmDelete").d("Confirm Delete"),
            text: intl.get("ConfirmDeletePatientStatusReason").d("Are you sure you want to delete this patient status reason?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (result.isConfirmed) {
            try {
                await fetchData(`/patient_status_reason/${reason.pk}`, "DELETE", true);
                Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: intl.get("PatientStatusReasonDeletedSuccessfully").d("Patient status reason deleted successfully"),
                });
                if (table.current) {
                    table.current.updateTable();
                }
                if (onReasonsChange) {
                    onReasonsChange();
                }
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: intl.get("FailedToDeletePatientStatusReason").d("Failed to delete patient status reason"),
                });
            }
        }
    }, [onReasonsChange]);

    const header = [
        {
            label: intl.get("Name").d("Name"),
            column_name: "name",
            filter: true,
            element: (row) => (
                <Typography fontWeight="500">{PatientStatusReason.getText(row.name)}</Typography>
            ),
        },
        {
            label: intl.get("Description").d("Description"),
            column_name: "description",
            filter: true,
            element: (row) => PatientStatusReason.getText(row.name) || "-",
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            width: "120px",
            sortable: false,
            filter: false,
            element: (row) => (
                <DmsPermissions permissions={[Permissions.SETTINGS_GENERAL]}>
                    <DmsButtonGroup>
                        <Tooltip title={intl.get("Edit").d("Edit")}>
                            <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(row)}
                                color="primary"
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={intl.get("Delete").d("Delete")}>
                            <IconButton
                                size="small"
                                onClick={() => handleDelete(row)}
                                color="error"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </DmsButtonGroup>
                </DmsPermissions>
            ),
        },
    ];

    const toolbar = (
        <DmsFlexContainer>
            <DmsFlexContainer.Left>
                <DmsPermissions permissions={[Permissions.SETTINGS_GENERAL]}>
                    <DmsButton
                        icon={<AddIcon />}
                        text={intl.get("CreatePatientStatusReason").d("Create Patient Status Reason")}
                        onClick={handleOpenCreate}
                        variant="primary"
                    />
                </DmsPermissions>
            </DmsFlexContainer.Left>
        </DmsFlexContainer>
    );

    return (
        <>
            <DmsTable
                header={header}
                managed={true}
                needs_authentication={true}
                ref={table}
                url="/patient_status_reason/table"
                toolbar={toolbar}
                export_table={false}
            />

            <PatientStatusReasonFormModal
                isVisible={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
                reason={selectedReason}
            />
        </>
    );
};

export default PatientStatusReasonTable;
